import React from 'react';

import PropTypes from 'prop-types';
import emptyFunction from '@avtechsoftware/ra-javascript-utilities/src/utilities/EmptyFunction';
import envConfig from '../../envConfig';

const PublicContext = React.createContext();

PublicContext.displayName = 'ThresholdRangeContext';

class PublicContextProvider extends React.Component {
  constructor(props) {
    super(props);
    const { remoteService } = this.props;
    const sessionHash = !envConfig.useMockSession ? this.getCookie('ADN_SESSION') : envConfig.raaToken;
    this.state = {
      hasAuthenticatedSession: false,
    };
    remoteService.setNonBearerAuthToken(sessionHash);
    this.getCookie = this.getCookie.bind(this);
    this.hasAdnSession = this.hasAdnSession.bind(this);
  }

  // eslint-disable-next-line class-methods-use-this
  getCookie(cname) {
    const name = `${cname}=`;
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i += 1) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return 'invalid';
  }

  hasAdnSession() {
    const { remoteService } = this.props;
    return remoteService.get(`${envConfig.apiUrl}/api/v1/session`)
      .then(() => true).catch(() => false);
  }

  contextValue() {
    const { remoteService } = this.props;
    const mutators = {
      hasAdnSession: this.hasAdnSession,
    };
    return { ...this.state, ...mutators, remoteService };
  }

  render() {
    const { children } = this.props;
    return (
      <PublicContext.Provider value={{ publicContext: this.contextValue() }}>
        {children}
      </PublicContext.Provider>
    );
  }
}

PublicContextProvider.sampleValue = (customizations) => {
  const provider = new PublicContextProvider({
    remoteService: {
      onAuthError: emptyFunction,
      setAuthToken: emptyFunction,
      setNonBearerAuthToken: emptyFunction,
      get: emptyFunction,
      getWithInactiveParam: emptyFunction,
      post: emptyFunction,
      patch: emptyFunction,
      delete: emptyFunction,
    },
  });
  provider.state = { ...provider.state, ...customizations };
  return {
    ...provider.contextValue(),
    ...customizations,
  };
};

PublicContextProvider.propTypes = {
  remoteService: PropTypes.shape({
    get: PropTypes.func.isRequired,
    post: PropTypes.func.isRequired,
    patch: PropTypes.func.isRequired,
    delete: PropTypes.func.isRequired,
    getWithInactiveParam: PropTypes.func.isRequired,
    setAuthToken: PropTypes.func.isRequired,
    setNonBearerAuthToken: PropTypes.func.isRequired,
    onAuthError: PropTypes.func.isRequired,
  }).isRequired,
};

const withPublicContext = (Component) => (props) => (
  <PublicContext.Consumer>
    {(context) => {
      if (typeof context === 'undefined') {
        throw new Error(`PublicContext not available when rendering ${Component.name}.`);
      }
      return <Component {...props} {...context} />;
    }}
  </PublicContext.Consumer>
);

const publicContextPropType = PropTypes.shape(
  {
    hasAdnSession: PropTypes.func.isRequired,
  },
);

export {
  PublicContextProvider,
  withPublicContext,
  publicContextPropType,
};

export default PublicContextProvider;
